import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { personasAPI } from '../services/api';

const PersonDetail = () => {
  const { id } = useParams();
  const [person, setPerson] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPersonDetails();
  }, [id]);

  const loadPersonDetails = async () => {
    try {
      setLoading(true);
      const [personResponse, initiativesResponse, summaryResponse] = await Promise.all([
        personasAPI.getById(id),
        personasAPI.getIniciativas(id),
        personasAPI.getResumen(id)
      ]);
      
      setPerson(personResponse.data);
      setInitiatives(initiativesResponse.data);
      setSummary(summaryResponse.data);
    } catch (err) {
      setError('Error al cargar los detalles de la persona');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'No definido';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getRoleColor = (role) => {
    const colors = {
      'responsable_comercial': '#dc3545',
      'lider_tecnico': '#666666',
      'jefe_proyecto': '#dc3545',
      'desarrollador': '#666666',
      'analista': '#888888',
      'consultor': '#dc3545',
      'stakeholder': '#666666',
      'colaborador': '#888888',
      'otro': '#000000'
    };
    return colors[role] || '#666666';
  };

  if (loading) {
    return <div className="loading">Cargando detalles de la persona...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  if (!person) {
    return <div className="alert alert-error">Persona no encontrada</div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1>{person.get_full_name || `${person.nombre} ${person.apellido}`}</h1>
          <p className="text-muted">{person.area_nombre || 'Sin área asignada'}</p>
        </div>
        <Link to="/people" className="btn btn-secondary">
          ← Volver a Personas
        </Link>
      </div>

      <div className="row">
        {/* Personal Information */}
        <div className="col-md-4">
          <div className="card mb-4">
            <div className="card-header">
              <h3 className="card-title">Información Personal</h3>
            </div>
            <div>
              <div className="mb-3">
                <strong>Nombre Completo:</strong>
                <p>{person.get_full_name || `${person.nombre} ${person.apellido}`}</p>
              </div>

              <div className="mb-3">
                <strong>Email:</strong>
                <p>
                  <a href={`mailto:${person.email}`} style={{ color: '#dc3545' }}>
                    {person.email}
                  </a>
                </p>
              </div>

              <div className="mb-3">
                <strong>Área:</strong>
                <p>{person.area_nombre || 'Sin área asignada'}</p>
              </div>

              <div className="mb-3">
                <strong>ID:</strong>
                <p className="text-muted">#{person.id}</p>
              </div>

              <div className="mb-3">
                <strong>Fecha de Registro:</strong>
                <p>{formatDate(person.created_at)}</p>
              </div>
            </div>
          </div>

          {/* Activity Summary */}
          {summary && (
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Resumen de Actividad</h4>
              </div>
              <div>
                <div className="row text-center">
                  <div className="col-12 mb-3">
                    <h3 style={{ color: '#dc3545', margin: 0 }}>
                      {summary.iniciativas_activas}
                    </h3>
                    <small className="text-muted">Proyectos Activos</small>
                  </div>
                </div>

                <div className="row text-center">
                  <div className="col-6 mb-3">
                    <h4 style={{ color: '#666666', margin: 0 }}>
                      {Math.round(summary.horas_semana_actual * 100) / 100}h
                    </h4>
                    <small className="text-muted">Esta Semana</small>
                  </div>
                  <div className="col-6 mb-3">
                    <h4 style={{ color: '#dc3545', margin: 0 }}>
                      {Math.round(summary.horas_mes_actual * 100) / 100}h
                    </h4>
                    <small className="text-muted">Este Mes</small>
                  </div>
                </div>

                {summary.roles_principales && summary.roles_principales.length > 0 && (
                  <div className="mb-3">
                    <strong>Roles Principales:</strong>
                    <div className="mt-2">
                      {summary.roles_principales.map((rol, index) => (
                        <span 
                          key={index}
                          style={{
                            backgroundColor: getRoleColor(rol),
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            marginRight: '5px',
                            marginBottom: '5px',
                            display: 'inline-block'
                          }}
                        >
                          {rol.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Projects */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Proyectos Asignados</h3>
              <p className="card-subtitle">
                {initiatives.length} proyecto{initiatives.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div>
              {initiatives.length === 0 ? (
                <div className="alert alert-info">
                  Esta persona no está asignada a ningún proyecto actualmente.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Proyecto</th>
                        <th>Negocio</th>
                        <th>Tecnología</th>
                        <th>Fecha Entrega</th>
                        <th>Equipo</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {initiatives.map(initiative => (
                        <tr key={initiative.id}>
                          <td>
                            <Link 
                              to={`/projects/${initiative.id}`}
                              style={{ textDecoration: 'none' }}
                            >
                              <strong>{initiative.nombre_de_la_oportunidad}</strong>
                            </Link>
                            {initiative.descripcion && (
                              <div style={{ 
                                fontSize: '12px', 
                                color: '#666', 
                                marginTop: '2px',
                                maxHeight: '40px',
                                overflow: 'hidden'
                              }}>
                                {initiative.descripcion.length > 80 
                                  ? initiative.descripcion.substring(0, 80) + '...' 
                                  : initiative.descripcion}
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ fontSize: '14px' }}>
                              {initiative.negocio}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {initiative.subnegocio}
                            </div>
                          </td>
                          <td>{initiative.tecnologia_nombre || 'No asignada'}</td>
                          <td>{formatDate(initiative.fecha_de_entrega_propuesta_prevista)}</td>
                          <td>
                            <span style={{ 
                              background: '#dc3545', 
                              color: 'white', 
                              padding: '2px 6px', 
                              borderRadius: '12px', 
                              fontSize: '12px' 
                            }}>
                              {initiative.total_personas || 0} persona{(initiative.total_personas || 0) !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td>
                            <Link 
                              to={`/projects/${initiative.id}`}
                              className="btn btn-sm btn-primary"
                            >
                              Ver Proyecto
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card mt-4">
            <div className="card-header">
              <h4 className="card-title">Acciones Rápidas</h4>
            </div>
            <div>
              <div className="d-flex flex-wrap">
                <Link to="/time-tracking" className="btn btn-primary mr-2 mb-2">
                  ⏰ Imputar Horas
                </Link>
                <a 
                  href={`mailto:${person.email}`} 
                  className="btn btn-secondary mr-2 mb-2"
                >
                  ✉️ Enviar Email
                </a>
                {person.area && (
                  <button 
                    className="btn btn-secondary mr-2 mb-2"
                    onClick={() => {
                      // This would filter people by area - could navigate to people list with area filter
                      window.location.href = `/people?area=${person.area}`;
                    }}
                  >
                    👥 Ver Colegas del Área
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonDetail;